/**
 * @file useIsScreenSize.ts
 *
 * @fileoverview Helps determine screen size using matchmedia.
 */

import { useEffect, useState } from "react";
import { ScreenSizeMap, ScreenSizeType } from "../theme/breakpoints";

/**
 * Hook returning a boolean value for a certain screen width match.
 *
 * @param {ScreenSizeType} maxScreenSize
 *  Theme breakpoint index that will be used for matching media max-width.
 * @param {ScreenSizeType} minScreenSize
 * Theme breakpoint index that will be used for matching media min-width.
 * @returns  {boolean}
 * True if media query is matched, false otherwise.
 */
export const useIsScreenSize = (
  maxScreenSize: ScreenSizeType,
  minScreenSize?: ScreenSizeType,
): boolean => {
  const isClient = typeof window === "object";
  const [isScreenSize, setIsScreenSize] = useState(true);

  function getIsScreenSize() {
    const matchMaxWidth = window.matchMedia(
      `(max-width: ${ScreenSizeMap[maxScreenSize] - 1}px)`,
    ).matches;
    const matchMinWidthIfDefined = !!minScreenSize
      ? window.matchMedia(`(min-width: ${ScreenSizeMap[minScreenSize]}px`)
          .matches
      : true;

    setIsScreenSize(isClient && matchMaxWidth && matchMinWidthIfDefined);
  }

  useEffect(() => {
    getIsScreenSize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getIsScreenSize, handleResize]);

  function handleResize() {
    getIsScreenSize();
  }

  return isScreenSize;
};
